import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Layout/Sidebar';
import Navbar from './components/Layout/Navbar';
import Loader from './components/Layout/Loader';
import Dashboard from './components/Dashboard/Dashboard';
import RecordManagement from './components/Records/RecordManagement';
import OrganizationManagement from './components/Organizations/OrganizationManagement';
import ContactManagement from './components/Contacts/ContactManagement';
import ProjectManagement from './components/Projects/ProjectManagement';
import StockManagement from './components/Stock/StockManagement';
import TenderManagement from './components/Tenders/TenderManagement';
import QuotationManagement from './components/Quotations/QuotationManagement';
import DeliveryChallanManagement from './components/Sales/DeliveryChallanManagement';
import InvoiceManagement from './components/Sales/InvoiceManagement';
import Reports from './components/Reports/Reports';
import UserManagement from './components/Users/UserManagement';
import CompanyProfile from './components/Company/CompanyProfile';
import ActivityLogs from './components/ActivityLogs/ActivityLogs';
import Login from './components/Auth/Login';
import { authService, companyService } from './services/api'; // Ensure this is exported in api.js
import ProtectedRoute from './components/Auth/ProtectedRoute';
import './App.css';

function App() {
  // Check if user is already logged in (from localStorage or session)
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // For demo mode, we can default to true or let user login with anything
    const savedAuth = localStorage.getItem('rsms_auth');
    return savedAuth === 'true';
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem('rsms_user');
    return savedUser ? JSON.parse(savedUser) : {
      name: 'Administrator',
      role: 'Administrator',
      email: 'admin@rsms.com',
      username: 'admin'
    };
  });

  const handleLogin = (userData, token) => {
    setCurrentUser(userData);
    setIsLoading(true);
    setShowLoader(true);
    localStorage.setItem('rsms_auth', 'true');
    localStorage.setItem('rsms_user', JSON.stringify(userData));
    if (token) {
      localStorage.setItem('rsms_token', token);
    }

    // Show loader for 5 seconds before showing the app
    setTimeout(() => {
      setIsAuthenticated(true);
      // Fade out loader
      setTimeout(() => {
        setShowLoader(false);
        setIsLoading(false);
      }, 500);
    }, 5000);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('rsms_auth');
    localStorage.removeItem('rsms_user');
    localStorage.removeItem('rsms_token');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Demo mode: simplified user refresh
  useEffect(() => {
    if (isAuthenticated && currentUser && currentUser.id) {
      // Just ensuring mock user is in sync
      const savedUser = localStorage.getItem('rsms_user');
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        if (parsedUser.id !== currentUser.id) {
          setCurrentUser(parsedUser);
        }
      }
    }
  }, [isAuthenticated, currentUser?.id]);

  // Fetch Company Profile for Title and Favicon
  const [companyProfile, setCompanyProfile] = useState(null);

  const fetchCompanyProfile = async () => {
    try {
      const res = await companyService.getProfile();
      if (res.data) {
        setCompanyProfile(res.data);
        if (res.data.name) document.title = res.data.name;
        if (res.data.logo) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
          }
          link.href = res.data.logo;
        }
      }
    } catch (err) {
      console.error("Failed to load company profile for branding", err);
    }
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, [isAuthenticated, currentUser?.id]); // Re-fetch when user logs in or data changes

  return (
    <Router>
      {showLoader && <Loader />}
      {!isAuthenticated && !isLoading ? (
        <Login onLogin={handleLogin} />
      ) : isAuthenticated && !showLoader ? (
        <div className="app-container">
          <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} user={currentUser} companyProfile={companyProfile} />
          <div className={`main-content ${isSidebarOpen ? 'sidebar-open' : ''}`}>
            <Navbar
              user={currentUser}
              onLogout={handleLogout}
              toggleSidebar={toggleSidebar}
              companyProfile={companyProfile}
            />
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/records" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><RecordManagement user={currentUser} /></ProtectedRoute>} />
              <Route path="/organizations" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><OrganizationManagement user={currentUser} /></ProtectedRoute>} />
              <Route path="/contacts" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><ContactManagement user={currentUser} /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><ProjectManagement user={currentUser} /></ProtectedRoute>} />
              <Route path="/stock" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><StockManagement user={currentUser} /></ProtectedRoute>} />
              <Route path="/tenders" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><TenderManagement user={currentUser} /></ProtectedRoute>} />
              <Route path="/quotations" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><QuotationManagement user={currentUser} /></ProtectedRoute>} />

              {/* Sales Module Routes */}
              <Route path="/sales/delivery-challans" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><DeliveryChallanManagement /></ProtectedRoute>} />
              <Route path="/sales/invoices" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><InvoiceManagement /></ProtectedRoute>} />

              <Route path="/reports" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Employee', 'Auditor']}><Reports user={currentUser} /></ProtectedRoute>} />
              <Route path="/company" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Auditor']}><CompanyProfile user={currentUser} onProfileUpdate={fetchCompanyProfile} /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Auditor']}><UserManagement user={currentUser} /></ProtectedRoute>} />
              <Route path="/activity-logs" element={<ProtectedRoute user={currentUser} roles={['Administrator', 'Manager', 'Auditor']}><ActivityLogs user={currentUser} /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <footer className="footer bg-white shadow-sm mt-auto py-3 border-top">
              <div className="container-fluid text-center">
                <span className="text-muted">
                  &copy; {new Date().getFullYear()} <a href="https://cubetechwiz.com/" target="_blank" rel="noopener noreferrer" className="text-muted text-decoration-none fw-bold">Cube Techwiz</a>. All Rights Reserved.
                </span>
              </div>
            </footer>
          </div>
        </div>
      ) : null}
    </Router>
  );
}

export default App;
